import React, { useContext, useEffect, useState } from "react";
import { HeaderData } from "@/app/(general-header)/header/types";
import CountdownTimer from "../../components/Countdown/countdown";
import useXcCompetition from "@/app/(custom-header)/prizes/car-v2/useXcCompetition";
import { setActiveCompetitions } from "@/app/redux/features/categories/actions";
import { getHeaderData } from "@/app/(general-header)/header/header-service";
import { setFeaturedImage } from "@/app/redux/features/categories/slice";
import { publishUserData, setHeaderIsLoading, setIsRafEnabled } from "@/app/redux/features/header/headerSlice";
import { useAppDispatch } from "@/app/redux/hooks";
import PastDueNotification from "@/app/(general-header)/header/pastDueNotification";
import EnterNowButton from "@/app/(custom-header)/prizes/car-v2/EnterNowButton";
import { CarCompetitionData } from "@/app/(custom-header)/prizes/car-v2/contextProvider";

const CarCustomHeader = () => {
  const [header, setHeader] = useState<HeaderData>();
  const [, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();
  const [carouselWasScrolled, setCarouselWasScrolled] = useState(false);

  useEffect(() => {
    const activeCompetitionsPromise = dispatch(setActiveCompetitions());
    activeCompetitionsPromise
      .unwrap()
      .then(res => {
        const syncEvent = new CustomEvent("sync-header-store", {
          detail: res,
        });
        window.dispatchEvent(syncEvent);
      })
      .catch(err => console.error(err));

    return () => {
      activeCompetitionsPromise.abort();
    };
  }, []);

  const fetchHeader = async () => {
    try {
      const response = await getHeaderData();
      const headerData = (await response.json()) as HeaderData;
      dispatch(setFeaturedImage(headerData.featuredCategoryImage));
      dispatch(setIsRafEnabled(headerData.IsRAFEnabled));
      const syncStoresEvent = new CustomEvent("sync-second-store", {
        detail: headerData,
      });
      window.dispatchEvent(syncStoresEvent);
      setHeader(headerData);
      dispatch(publishUserData({ userData: headerData?.MyAccountMenuBar }));
    } catch (err) {
      console.log(`Error while fetching the header data - ${JSON.stringify(err)}`);
    } finally {
      setIsLoading(false);
      dispatch(setHeaderIsLoading(false));
    }
  };

  useEffect(() => {
    fetchHeader();
  }, []);

  const getBackButton = () => (
    <svg
      className="cursor-pointer"
      onClick={() => {
        window.location.href = "/";
      }}
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="29"
      viewBox="0 0 28 29"
      fill="none"
      aria-label="Go to homepage"
      role="button">
      <path
        d="M6.66602 16H25.3327M6.66602 16L14.666 24M6.66602 16L14.666 8"
        stroke="#4C4C52"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  useEffect(() => {
    const onMoveButtonUp = event => {
      if (!event.detail) {
        setCarouselWasScrolled(true);
      } else {
        setCarouselWasScrolled(false);
      }
    };
    window.addEventListener("moveButtonUp", onMoveButtonUp);

    return () => {
      window.removeEventListener("moveButtonUp", onMoveButtonUp);
    };
  }, []);

  const carCompetition = useXcCompetition();
  const { countdownText, competitionEndDate } = useContext(CarCompetitionData);
  const endDateTime = competitionEndDate || carCompetition.EndDateTime;

  return (
    <>
      <header
        className={
          "lg:sticky top-0 bg-white z-[1002] lg:!border-b-[#E0E0E0] lg:!border-b lg:border-solid h-[56px] lg:h-[60px]"
        }>
        <div
          className={`flex items-center justify-between px-3 lg:px-6 relative mainSection mx-auto border-none h-full`}>
          <div>{getBackButton()}</div>
          <img
            src="/assets/images/car-competition/luxury-cars-header-badge.svg"
            alt="BOTB Luxury Cars Draw"
            className="w-[90px] lg:w-[120px] top-4 absolute left-1/2 -translate-x-1/2 z-[1]"
          />
          <div className={"font-jost hidden lg:flex items-center gap-4 grow justify-end"}>
            <div id={"top_counter"} className="flex flex-col items-start gap-1 text-center">
              <div className={"text-secondary text-base leading-5.5 font-semibold tracking-[0] w-full"}>
                {countdownText || carCompetition.SubtitleText || carCompetition.Title}
              </div>
              {endDateTime ? (
                <CountdownTimer
                  EndDateTime={new Date(endDateTime)}
                  textClass={"uppercase text-secondary text-base leading-5.5 font-normal mr-1"}
                  numericClass={"text-primary font-bold text-base leading-5.5"}
                  containerClass={"flex items-center justify-center gap-[2px] leading-none w-full"}
                  format="d:h:m:s"
                />
              ) : null}
            </div>
            <div
              className={`overflow-hidden transition-[width] duration-700 ease-in-out`}
              style={{ width: carouselWasScrolled ? "160px" : "0" }}>
              {carouselWasScrolled && <EnterNowButton variation={"desktop_sticky"} place="car_new_floating_cta" />}
            </div>
          </div>
        </div>
      </header>
      {header?.ShowPastDueBanner ? <PastDueNotification onSnooze={fetchHeader} /> : null}
    </>
  );
};

export default CarCustomHeader;
